import { useState } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import Loader from "../../components/Loader";
import useGetPatientRewards from "../../hooks/useGetPatientRewards";
import { formatDate } from "../../utils/helpers";

const headerCellSx = {
  backgroundColor: (theme) => theme.palette.grey[100],
  fontWeight: "bold",
  textTransform: "uppercase",
  fontSize: "0.85rem",
  color: "#424242",
  whiteSpace: "nowrap",
};

const bodyCellSx = { whiteSpace: "nowrap" };

const StatCard = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ px: 3, py: 2, minWidth: 160, textAlign: "center" }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight="bold" color={color}>
      {value}
    </Typography>
  </Paper>
);

const Rewards = () => {
  const { _id_ } = useParams();
  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext();

  setActive("reward");
  setBreadcrumbTitle("Rewards");

  const [typeFilter, setTypeFilter] = useState("all");

  const { data, isLoading } = useGetPatientRewards({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const transactions = data?.transactions ?? [];
  const filtered =
    typeFilter === "all" ? transactions : transactions.filter((t) => t.type === typeFilter);

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Rewards</Typography>
      </Stack>

      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
        <StatCard label="Total Earned" value={data?.total_earned ?? 0} color="success.main" />
        <StatCard label="Total Redeemed" value={data?.total_redeemed ?? 0} color="error.main" />
        <StatCard label="Balance" value={data?.balance ?? 0} color="primary.main" />
      </Stack>

      <Box mb={2}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="earn">Earned</MenuItem>
            <MenuItem value="redeem">Redeemed</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Type</TableCell>
              <TableCell sx={headerCellSx}>Points</TableCell>
              <TableCell sx={headerCellSx}>Session #</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              filtered.map((txn) => (
                <TableRow key={txn.id} hover>
                  <TableCell sx={bodyCellSx}>{formatDate(txn.created_at)}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Chip
                      label={txn.type === "earn" ? "Earned" : "Redeemed"}
                      size="small"
                      color={txn.type === "earn" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Typography
                      component="span"
                      fontWeight="bold"
                      color={txn.type === "earn" ? "success.main" : "error.main"}
                    >
                      {txn.type === "earn" ? "+" : "−"}
                      {txn.points}
                    </Typography>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{txn.session_number ?? "—"}</TableCell>
                </TableRow>
              ))}

            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No reward transactions found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default Rewards;
